import {useLearningStore} from "~/store/learning/learningStore";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import {applicationContext} from "@@/package/application/applicationContext";

/**
 * AWS Credentials 관리 서비스
 * Cognito Identity를 사용하여 임시 자격 증명을 생성하고 관리합니다.
 */
export const useAwsCredentialsService = () => {
    const config = { public: applicationContext.getConfig() };
    const learningStore = useLearningStore();

    /**
     * AWS Cognito Identity Credentials 생성
     * 클라이언트 사이드에서만 실행됩니다.
     * @returns Promise<boolean> - 생성 성공 여부
     */
    const createCredentials = async (): Promise<boolean> => {
        // 서버 사이드에서는 실행하지 않음
        if (typeof window === 'undefined') {
            return false;
        }

        // 이미 초기화되어 있으면 true 반환
        if (learningStore.directory.isInitCredentials) {
            return true;
        }

        try {
            // Create credential provider
            const credentialProvider = fromCognitoIdentityPool({
                clientConfig: { region: 'ap-northeast-2' },
                identityPoolId: config.public.AWS_IDENTITY_POLL_ID
            });

            // Resolve credentials
            const resolved = await credentialProvider();

            // Store both resolved credentials and provider
            learningStore.directory.credentials = {
                accessKeyId: resolved.accessKeyId,
                secretAccessKey: resolved.secretAccessKey,
                sessionToken: resolved.sessionToken
            };
            learningStore.directory.credentialProvider = credentialProvider;
            learningStore.directory.isInitCredentials = true;

            // 50분 후 자격 증명 갱신
            setTimeout(() => {
                if (resolved.accessKeyId === learningStore.directory.credentials?.accessKeyId) {
                    createCredentials();
                }
            }, 50 * 60 * 1000);

            return true;
        } catch (error) {
            console.error('Error creating AWS credentials:', error);
            return false;
        }
    };

    /**
     * Credentials가 초기화되어 있는지 확인
     */
    const isCredentialsInitialized = (): boolean => {
        return learningStore.directory.isInitCredentials;
    };

    /**
     * Credentials가 없으면 생성하고, 있으면 그대로 반환
     */
    const ensureCredentials = async (): Promise<boolean> => {
        if (isCredentialsInitialized()) {
            return true;
        }
        return await createCredentials();
    };

    return {
        createCredentials,
        isCredentialsInitialized,
        ensureCredentials
    };
};

